import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Card,
  CardContent,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import axios from 'axios';
import { Search, Refresh as RefreshIcon } from '@mui/icons-material';
import { getAssociates } from '../../services/associate';
import { getToken } from '../../services/authAPIS';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsExcel, exportAsCSV } from '../../utils/importExport';

const CommissionReport = () => {
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filter Input State
  const [filterInputs, setFilterInputs] = useState({
    associate: '', 
    fromDate: '',
    toDate: '',
  });

  // Active Filter State (Sent to API)
  const [activeFilters, setActiveFilters] = useState({
    associate: '', 
    fromDate: '',
    toDate: '',
  });

  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  
  // Data State
  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionsError, setCommissionsError] = useState('');

  // 1. Load Associates for Dropdown
  useEffect(() => {
    let cancelled = false;
    const loadAssociates = async () => {
      setAssociatesLoading(true);
      try {
        const response = await getAssociates({ page: 1, limit: 10 });
        const list = Array.isArray(response?.data) ? response.data : [];
        if (!cancelled) setAssociates(list);
      } catch (error) {
        console.error('Failed to fetch associates', error);
      } finally {
        if (!cancelled) setAssociatesLoading(false);
      }
    };
    loadAssociates();
    return () => { cancelled = true; };
  }, []);

  // 2. Fetch Report Data
  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setCommissionsLoading(true);
      setCommissionsError('');
      
      try {
        const params = new URLSearchParams();
        params.append('page', page + 1);
        params.append('limit', rowsPerPage);

        if (activeFilters.associate) params.append('associateId', activeFilters.associate);
        if (activeFilters.fromDate) params.append('fromDate', activeFilters.fromDate);
        if (activeFilters.toDate) params.append('toDate', activeFilters.toDate);

        const token = getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/associate-commissions/commissionReport`,
          {
            params,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        if (cancelled) return;

        const rawData = res?.data?.data || [];
        const totalRecords = res?.data?.total || res?.data?.count || 0;
        const list = Array.isArray(rawData) ? rawData : [];

        const normalized = list.map((item, idx) => {
          const paid = Number(item.paidAmount || 0);
          const due = Number(item.dueAmount || 0);
          const commissionAmt = Number(item.commissionAmount || 0);
          const status = due > 0 ? 'Pending' : 'Paid';

          return {
            sno: (page * rowsPerPage) + (idx + 1),
            associateId: item.associateId || '-',
            associateObjectId: item.associateObjectId || '',
            associateName: item.associateName || '-',
            commissionRate: Number(item.commissionPercent || 0),
            commissionAmount: commissionAmt,
            paidAmount: paid,
            dueAmount: due,
            status: status,
          };
        });

        setCommissions(normalized);
        setTotalCount(totalRecords);

      } catch (err) {
        if (!cancelled) {
          const msg = err?.response?.data?.message || err?.message || 'Failed to fetch commission report';
          setCommissionsError(msg);
        }
      } finally {
        if (!cancelled) setCommissionsLoading(false);
      }
    };

    fetchReport();

    return () => { cancelled = true; };
  }, [page, rowsPerPage, activeFilters]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [activeFilters.associate, activeFilters.fromDate, activeFilters.toDate]);

  // Handle Search Button Click
  const handleSearch = () => {
    // Reset page and update filters in a single batch to avoid race conditions
    setPage(0);
    setActiveFilters({ ...filterInputs });
  };

  const handleReset = () => {
    const defaultFilters = {
      associate: '',
      fromDate: '',
      toDate: '',
    };
    setFilterInputs(defaultFilters);
    setActiveFilters(defaultFilters);
    setPage(0);
  };

  // Associate Autocomplete Helpers
  const associateOptions = useMemo(() => {
    const opts = (associates || []).map((assoc) => {
      const value = assoc?._id || '';
      const code = assoc?.associateId || '';
      const name = assoc?.name || 'Unnamed';
      return { 
        label: code ? `${name} (${code})` : name, 
        value,
        code,
        name, 
      };
    });
    return [{ label: 'All Associates', value: '', name: '' }, ...opts];
  }, [associates]);

  const associateFilterOptions = createFilterOptions({
    stringify: (option) => `${option.label} ${option.value}`,
  });

  // Build rows shared by all export formats
  const buildExportRows = () =>
    commissions.map(item => ({
      'S.No': item.sno,
      'Associate ID': item.associateId,
      'Associate Name': item.associateName,
      'Comm %': item.commissionRate,
      'Total Commission': item.commissionAmount,
      'Paid Amount': item.paidAmount,
      'Due Amount': item.dueAmount,
      'Status': item.status,
    }));

  const handleExportPDF = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsPDF({ rows, title: 'Commission Report', fileNameBase: 'commission_report' });
  };

  const handleExportCSV = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsCSV({ rows, fileNameBase: 'commission_report' });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsExcel({ rows, sheetName: 'Commission Report', fileNameBase: 'commission_report' });
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>Commission Report</Typography>
          <Typography variant="body2" color="text.secondary">Track commission payments and dues</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          <ExportButton
            onExportPdf={handleExportPDF}
            onExportCsv={handleExportCSV}
            onExportExcel={handleExportExcel}
            variant="contained"
          />
          <RefreshButton
            onClick={() => setActiveFilters({ ...activeFilters })}
            loading={commissionsLoading}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 40 }
            }}
          />
        </Box>
      </Box>

      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Associate Search */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={associateOptions}
                filterOptions={associateFilterOptions}
                value={associateOptions.find(o => o.value === filterInputs.associate) || associateOptions[0]}
                onChange={(_, newValue) => {
                  const selectedVal = newValue?.value || '';
                  setFilterInputs(prev => ({ ...prev, associate: selectedVal }));
                  setActiveFilters(prev => ({ ...prev, associate: selectedVal }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Search Associate" 
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {associatesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Date Filters */}
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                type="date" 
                label="From" 
                InputLabelProps={{ shrink: true }} 
                value={filterInputs.fromDate} 
                onChange={(e) => setFilterInputs({ ...filterInputs, fromDate: e.target.value })} 
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                type="date" 
                label="To" 
                InputLabelProps={{ shrink: true }} 
                value={filterInputs.toDate} 
                onChange={(e) => setFilterInputs({ ...filterInputs, toDate: e.target.value })} 
              />
            </Grid>

            {/* SEARCH & RESET BUTTONS */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  fullWidth
                  variant="contained" 
                  onClick={handleSearch}
                  startIcon={<Search />}
                  disabled={commissionsLoading}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  {commissionsLoading ? 'Searching...' : 'Search'}
                </Button>
                <Button 
                  fullWidth
                  variant="outlined" 
                  onClick={handleReset}
                  disabled={commissionsLoading}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer
        component={Paper}
        key={`table-${activeFilters.associate}-${activeFilters.fromDate}-${activeFilters.toDate}`}
        sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: '#536DFE' }}>
            <TableRow>
              {["S.No", "Associate ID", "Associate Name", "Comm %", "Total Comm", "Paid", "Due", "Status"].map((head) => (
                <TableCell key={head} sx={{ color: '#fff', fontWeight: 700 }}>{head}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {commissionsError ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'error.main' }}>{commissionsError}</TableCell></TableRow>
            ) : commissionsLoading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : commissions.length > 0 ? (
              commissions.map((row) => (
                <TableRow key={row.associateObjectId + row.sno} hover>
                  <TableCell>{row.sno}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.associateId}</TableCell>
                  <TableCell>{row.associateName}</TableCell>
                  <TableCell>{row.commissionRate}%</TableCell>
                  <TableCell sx={{ color: '#1976d2', fontWeight: 700 }}>Rs. {Number(row.commissionAmount).toLocaleString()}</TableCell>
                  <TableCell sx={{ color: '#2e7d32' }}>Rs. {Number(row.paidAmount).toLocaleString()}</TableCell>
                  <TableCell sx={{ color: '#d32f2f' }}>Rs. {Number(row.dueAmount).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.status} 
                      size="small" 
                      color={row.status === 'Paid' ? 'success' : 'warning'} 
                      variant={row.status === 'Paid' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No data found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          
          component="div"
          count={totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          disabled={commissionsLoading}
        />
      </TableContainer>
    </Box>
  );
};

export default CommissionReport;